import { useEffect, useState } from "react";
import { Link, useLocation } from "@tanstack/react-router";
import bigdeltaMark from "@/assets/bigdelta-mark.png";
import MetricsLibraryPreview from "@/components/MetricsLibraryPreview";
import FeaturedAppPreview from "@/components/FeaturedAppPreview";
import { trackSignUp } from "@/lib/tracking";

import {
  ChevronDown,
  LineChart,
  PlayCircle,
  Flame,
  Filter,
  Users,
  Bell,
  Plug,
  Menu,
  X,
  BookOpen,
  FileText,
  MessageCircle,
  Newspaper,
} from "lucide-react";
import { cn } from "@/lib/utils";

type FeatureColor =
  | "web"
  | "replays"
  | "heatmaps"
  | "funnels"
  | "profiles"
  | "notifications"
  | "integrations"
  | "library"
  | "docs"
  | "support"
  | "changelog";

const productItems = [
  {
    to: "/web-analytics",
    label: "Web analytics",
    description: "Visitors, pageviews and traffic sources in real time.",
    icon: LineChart,
    color: "web" as FeatureColor,
  },
  {
    to: "/session-replays",
    label: "Session replays",
    description: "Watch how real users experience your site.",
    icon: PlayCircle,
    color: "replays" as FeatureColor,
  },
  {
    to: "/heatmaps",
    label: "Heatmaps",
    description: "See where visitors click, scroll and get stuck.",
    icon: Flame,
    color: "heatmaps" as FeatureColor,
  },
  {
    to: "/funnels",
    label: "Funnels",
    description: "Find where visitors drop off before converting.",
    icon: Filter,
    color: "funnels" as FeatureColor,
  },
  {
    to: "/profiles",
    label: "Profiles",
    description: "Know every visitor and account, end to end.",
    icon: Users,
    color: "profiles" as FeatureColor,
  },
  {
    to: "/integrations",
    label: "Integrations",
    description: "Connect billing, CRM and product data sources.",
    icon: Plug,
    color: "integrations" as FeatureColor,
  },
];

const resourcesItems = [
  {
    to: "http://docs.bigdelta.com",
    label: "Docs",
    description: "Install guides, API references and feature documentation.",
    icon: FileText,
    color: "docs" as FeatureColor,
    external: true,
  },
  {
    to: "/blog",
    label: "Blog",
    description: "Growth playbooks, metric guides and SaaS benchmarks.",
    icon: BookOpen,
    color: "library" as FeatureColor,
  },
  {
    to: "/support",
    label: "Support",
    description: "Get help from the Bigdelta team by chat or email.",
    icon: MessageCircle,
    color: "support" as FeatureColor,
  },
  {
    to: "https://docs.bigdelta.com/changelog",
    label: "Changelog",
    description: "Latest product updates, launches and improvements.",
    icon: Newspaper,
    color: "changelog" as FeatureColor,
    external: true,
  },
];

const featureIconClass = (color: FeatureColor, active: boolean) => {
  const base =
    "flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-background transition-colors";
  if (active) {
    switch (color) {
      case "web":
        return `${base} text-feature-web`;
      case "replays":
        return `${base} text-feature-replays`;
      case "heatmaps":
        return `${base} text-feature-heatmaps`;
      case "funnels":
        return `${base} text-feature-funnels`;
      case "profiles":
        return `${base} text-feature-profiles`;
      case "notifications":
        return `${base} text-feature-notifications`;
      case "integrations":
        return `${base} text-feature-integrations`;
      case "library":
        return `${base} text-feature-library`;
      case "docs":
        return `${base} text-feature-docs`;
      case "support":
        return `${base} text-feature-support`;
      case "changelog":
        return `${base} text-feature-changelog`;
    }
  }
  switch (color) {
    case "web":
      return `${base} text-feature-web group-hover:bg-foreground/[0.04]`;
    case "replays":
      return `${base} text-feature-replays group-hover:bg-foreground/[0.04]`;
    case "heatmaps":
      return `${base} text-feature-heatmaps group-hover:bg-foreground/[0.04]`;
    case "funnels":
      return `${base} text-feature-funnels group-hover:bg-foreground/[0.04]`;
    case "profiles":
      return `${base} text-feature-profiles group-hover:bg-foreground/[0.04]`;
    case "notifications":
      return `${base} text-feature-notifications group-hover:bg-foreground/[0.04]`;
    case "integrations":
      return `${base} text-feature-integrations group-hover:bg-foreground/[0.04]`;
    case "library":
      return `${base} text-feature-library group-hover:bg-foreground/[0.04]`;
    case "docs":
      return `${base} text-feature-docs group-hover:bg-foreground/[0.04]`;
    case "support":
      return `${base} text-feature-support group-hover:bg-foreground/[0.04]`;
    case "changelog":
      return `${base} text-feature-changelog group-hover:bg-foreground/[0.04]`;
  }
};

type MenuKey = "product" | "resources" | null;

const ItemLink = ({
  to,
  label,
  description,
  Icon,
  color,
  active,
  onClick,
  external,
}: {
  to: string;
  label: string;
  description: string;
  Icon: React.ComponentType<{ className?: string }>;
  color: FeatureColor;
  active: boolean;
  onClick: () => void;
  external?: boolean;
}) => {
  const className = cn(
    "group relative flex items-center gap-3 rounded-xl px-3 py-3 transition-colors outline-none",
    active ? "bg-foreground/[0.06]" : "hover:bg-foreground/[0.04] focus:bg-foreground/[0.04]",
  );
  const content = (
    <>
      <span className={featureIconClass(color, active)}>
        <Icon className="h-5 w-5" />
      </span>
      <span className="flex flex-col">
        <span className="text-sm font-medium leading-tight text-foreground">{label}</span>
        <span className="text-xs text-muted-foreground leading-snug mt-0.5">{description}</span>
      </span>
    </>
  );

  if (external) {
    return (
      <a
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClick}
        className={className}
      >
        {content}
      </a>
    );
  }

  return (
    <Link to={to} onClick={onClick} className={className}>
      {content}
    </Link>
  );
};

const Navbar = () => {
  const { pathname } = useLocation();
  const isProductActive = productItems.some((i) => pathname.startsWith(i.to));
  const isResourcesActive = resourcesItems.some((i) => pathname.startsWith(i.to));
  const isPricingActive = pathname.startsWith("/pricing");

  const [openMenu, setOpenMenu] = useState<MenuKey>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const close = () => setOpenMenu(null);
  const closeMobile = () => setMobileOpen(false);
  const toggleMobile = () => setMobileOpen((prev) => !prev);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  const linkClass = (active: boolean) =>
    cn(
      "px-4 py-2 rounded-full text-sm font-medium transition-colors outline-none",
      active
        ? "text-foreground bg-foreground/[0.06]"
        : "text-muted-foreground hover:text-foreground hover:bg-foreground/[0.05] focus:bg-foreground/[0.05]",
    );

  const mobileLinkClass = (active: boolean) =>
    cn(
      "block w-full text-left px-4 py-3 rounded-xl text-base font-medium transition-colors",
      active
        ? "text-foreground bg-foreground/[0.06]"
        : "text-muted-foreground hover:text-foreground hover:bg-foreground/[0.04]",
    );

  return (
    <nav className="fixed top-4 left-4 right-4 z-50 max-w-6xl mx-auto" onMouseLeave={close}>
      <div className="relative flex items-center justify-between px-5 py-2 bg-background/80 backdrop-blur-md border border-border rounded-2xl shadow-sm">
        <Link to="/" className="flex items-center gap-2">
          <img
            src={bigdeltaMark}
            alt="Bigdelta"
            width={24}
            height={24}
            className="h-6 w-6 rounded-md"
          />
          <span className="text-lg font-semibold text-foreground">Bigdelta</span>
        </Link>
        <div className="hidden md:flex items-center gap-1 text-sm">
          <button
            type="button"
            onMouseEnter={() => setOpenMenu("product")}
            onFocus={() => setOpenMenu("product")}
            className={cn("flex items-center gap-1", linkClass(isProductActive))}
          >
            Product <ChevronDown className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onMouseEnter={() => setOpenMenu("resources")}
            onFocus={() => setOpenMenu("resources")}
            className={cn("flex items-center gap-1", linkClass(isResourcesActive))}
          >
            Resources <ChevronDown className="h-3.5 w-3.5" />
          </button>
          <Link to="/pricing" onMouseEnter={close} className={linkClass(isPricingActive)}>
            Pricing
          </Link>
        </div>
        <div className="flex items-center gap-2 sm:gap-6">
          <a
            href="https://eu.bigdelta.com/login"
            onMouseEnter={close}
            className="hidden sm:inline-block text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Sign in
          </a>
          <a
            href="https://eu.bigdelta.com/sign-up"
            onClick={trackSignUp}
            onMouseEnter={close}
            className="text-sm bg-foreground text-background px-4 py-2 rounded-md font-medium hover:bg-foreground/90 transition-colors"
          >
            Get started
          </a>
          <button
            type="button"
            onClick={toggleMobile}
            className="md:hidden inline-flex items-center justify-center h-9 w-9 rounded-md text-foreground hover:bg-foreground/[0.06] transition-colors"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden absolute left-0 right-0 top-full pt-3 z-50">
          <div className="rounded-2xl border border-border/80 bg-popover text-popover-foreground shadow-xl p-3 flex flex-col gap-1">
            <span className="px-4 py-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-medium">
              Explore the product
            </span>
            {productItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={closeMobile}
                className={cn(
                  "flex items-center gap-3 px-4 py-3 rounded-xl transition-colors",
                  pathname === item.to ? "bg-foreground/[0.06]" : "hover:bg-foreground/[0.04]",
                )}
              >
                <span className={featureIconClass(item.color, pathname === item.to)}>
                  <item.icon className="h-5 w-5" />
                </span>
                <span className="text-sm font-medium text-foreground">{item.label}</span>
              </Link>
            ))}
            <div className="my-1 border-t border-border/60" />
            <span className="px-4 py-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-medium">
              Resources
            </span>
            {resourcesItems.map((item) => {
              const active = !item.external && pathname === item.to;
              const className = cn(
                "flex items-center gap-3 px-4 py-3 rounded-xl transition-colors",
                active ? "bg-foreground/[0.06]" : "hover:bg-foreground/[0.04]",
              );
              const content = (
                <>
                  <span className={featureIconClass(item.color, active)}>
                    <item.icon className="h-5 w-5" />
                  </span>
                  <span className="text-sm font-medium text-foreground">{item.label}</span>
                </>
              );
              return item.external ? (
                <a
                  key={item.to}
                  href={item.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeMobile}
                  className={className}
                >
                  {content}
                </a>
              ) : (
                <Link key={item.to} to={item.to} onClick={closeMobile} className={className}>
                  {content}
                </Link>
              );
            })}
            <Link
              to="/pricing"
              onClick={closeMobile}
              className={cn(mobileLinkClass(isPricingActive), "mt-1")}
            >
              Pricing
            </Link>
            <div className="mt-2 flex flex-col gap-2">
              <a
                href="https://eu.bigdelta.com/login"
                onClick={closeMobile}
                className="sm:hidden block w-full text-center px-4 py-3 rounded-xl text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-foreground/[0.04] transition-colors"
              >
                Sign in
              </a>
              <a
                href="https://eu.bigdelta.com/sign-up"
                onClick={() => {
                  trackSignUp();
                  closeMobile();
                }}
                className="block w-full text-center text-sm bg-foreground text-background px-4 py-3 rounded-md font-medium hover:bg-foreground/90 transition-colors"
              >
                Get started
              </a>
            </div>
          </div>
        </div>
      )}

      {openMenu && (
        <div
          className="absolute left-1/2 -translate-x-1/2 top-full pt-3 z-50"
          onMouseEnter={() => setOpenMenu(openMenu)}
        >
          {openMenu === "product" && (
            <div className="w-[640px] p-2 rounded-2xl border border-border/80 bg-popover text-popover-foreground shadow-xl">
              <div className="grid grid-cols-[1fr_240px] gap-2">
                <div className="grid gap-1">
                  <span className="px-3 py-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-medium">
                    Explore the product
                  </span>
                  {productItems.map((item) => (
                    <ItemLink
                      key={item.to}
                      to={item.to}
                      label={item.label}
                      description={item.description}
                      Icon={item.icon}
                      color={item.color}
                      active={pathname === item.to}
                      onClick={close}
                    />
                  ))}
                </div>

                <Link
                  to="/apps"
                  onClick={close}
                  className="group flex flex-col rounded-xl bg-foreground/[0.03] hover:bg-foreground/[0.06] transition-colors p-3 outline-none"
                >
                  <div className="rounded-lg overflow-hidden border border-border/60 bg-background">
                    <FeaturedAppPreview />
                  </div>
                  <span className="mt-3 text-[10px] uppercase tracking-[0.18em] text-primary font-medium">
                    Featured App
                  </span>
                  <span className="mt-1 text-sm font-medium text-foreground leading-snug">
                    Notifications
                  </span>
                  <span className="mt-1 text-xs text-muted-foreground leading-snug">
                    Get instant Slack or Discord notifications the moment someone signs up, places
                    an order or abandons checkout.
                  </span>
                  <span className="mt-2 text-xs text-muted-foreground inline-flex items-center gap-1 group-hover:text-foreground transition-colors">
                    See all apps →
                  </span>
                </Link>
              </div>
            </div>
          )}

          {openMenu === "resources" && (
            <div className="w-[640px] p-2 rounded-2xl border border-border/80 bg-popover text-popover-foreground shadow-xl">
              <div className="grid grid-cols-[1fr_240px] gap-2">
                <div className="grid gap-1">
                  <span className="px-3 py-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-medium">
                    Resources
                  </span>
                  {resourcesItems.map((item) => (
                    <ItemLink
                      key={item.to}
                      to={item.to}
                      label={item.label}
                      description={item.description}
                      Icon={item.icon}
                      color={item.color}
                      active={!item.external && pathname === item.to}
                      onClick={close}
                      external={item.external}
                    />
                  ))}
                </div>

                <a
                  href="/metrics"
                  onClick={close}
                  className="group flex flex-col rounded-xl bg-foreground/[0.03] hover:bg-foreground/[0.06] transition-colors p-3 outline-none"
                >
                  <div className="rounded-lg overflow-hidden border border-border/60 bg-background">
                    <MetricsLibraryPreview />
                  </div>
                  <span className="mt-3 text-[10px] uppercase tracking-[0.18em] text-primary font-medium">
                    New
                  </span>
                  <span className="mt-1 text-sm font-medium text-foreground leading-snug">
                    The SaaS metrics library — every metric, defined
                  </span>
                  <span className="mt-2 text-xs text-muted-foreground inline-flex items-center gap-1 group-hover:text-foreground transition-colors">
                    Explore the library →
                  </span>
                </a>
              </div>
            </div>
          )}
        </div>
      )}
    </nav>
  );
};

export default Navbar;
